import { Fragment, ReactNode, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Power, RefreshCw, Trash2, UserRound } from "lucide-react";

import { Alert } from "../../components/Alert/Alert";
import { AlertStack } from "../../components/Alert/AlertStack";
import { Avatar } from "../../components/Avatar/Avatar";
import { Badge } from "../../components/Badge/Badge";
import { Button } from "../../components/Button/Button";
import { CountryCell } from "../../components/CountryCell/CountryCell";
import { Input } from "../../components/Input/Input";
import { Menu } from "../../components/Menu/Menu";
import { Modal } from "../../components/Modal/Modal";
import { Select } from "../../components/Select/Select";
import { Table, type Column } from "../../components/Table/Table";
import { useToast } from "../../components/Toast/toastContext";
import { guildQueryKey, guildsListQueryKey } from "../guilds/queryKeys";
import { playerQueryKey, playersListQueryKey } from "../players/queryKeys";
import {
  applyBotProfile,
  getBotsOverview,
  listBotPool,
  removeBot,
  setBotEnabled,
  botError,
} from "./api";
import { sourceLink } from "./links";
import { ago } from "./relativeTime";
// The Source tab already decided how these numbers read — «34 rows left», with
// the same grouping. Two spellings of one count on two tabs is two tables.
import { formatCount, plural, SOURCE_LABEL } from "./roster";
import styles from "./BotsPage.module.css";
import type { ApplyProfileMode, BotSource, PoolBot } from "./types";

/**
 * The Pool tab: who we actually have.
 *
 * The worker fills the pool by itself; the three controls here are the manual
 * exceptions. Disable freezes a bot in place — it keeps its guild and
 * everything it has said. Remove takes it out of its guild and deletes the
 * player, but NOT its chat messages: deleting those would tear holes in a
 * history living players have read and replied to.
 *
 * The third is «Apply profile…». The profile rules run when the worker CREATES
 * a bot, so a pool that predates them stays empty for ever — rows of zeros
 * between real players in every roster. The Stats column is how that is seen
 * at all, and the dialog is the only way to fix it.
 * Mockups: bots-pool-apply-profile.html, bots-pool-reroll-avatars.html.
 */
export function PoolTab({ projectID }: { projectID: string }) {
  const qc = useQueryClient();
  const { push } = useToast();
  const [q, setQ] = useState("");
  const [profile, setProfile] = useState("");
  const [state, setState] = useState("");
  const [pendingRemoval, setPendingRemoval] = useState<PoolBot | null>(null);
  const [applyOpen, setApplyOpen] = useState(false);
  const [picked, setMode] = useState<ApplyProfileMode>("fill");

  const { data, isLoading } = useQuery({
    queryKey: ["project", projectID, "bots", "pool", q, profile, state],
    queryFn: () => listBotPool(projectID, { q, profile, state }),
    enabled: !!projectID,
  });

  // The same key BotsPage already runs, so this reads the cache rather than
  // the network. The counts it needs are pool-wide facts, and the pool list
  // only knows what the current filter matched.
  const {
    data: overviewData,
    isLoading: overviewLoading,
    isError: overviewFailed,
  } = useQuery({
    queryKey: ["project", projectID, "bots", "overview"],
    queryFn: () => getBotsOverview(projectID),
    enabled: !!projectID,
  });
  const o = overviewData?.overview;
  const total = o?.total ?? 0;
  // The banner's number and the dialog's are different questions, and the
  // wrong one in either place is a lie about what the button will do:
  // `bots_without_profile` is «never dealt a card», `bots_missing_keys` is
  // what the fill mode would actually write to.
  const withoutProfile = o?.bots_without_profile ?? 0;
  const missingKeys = o?.bots_missing_keys ?? 0;
  const inPopulated = o?.bots_in_populated_guilds ?? 0;
  // The bots no mode will touch. Their card was typed, not rolled, so the
  // dialog has to say so BEFORE the button is pressed — afterwards is a report
  // on something the operator did not agree to.
  const fromTable = o?.bots_from_table ?? 0;
  // Three states, not two. «No answer yet» and «the request failed» are both
  // «we do not know», and reporting either as «this project has no rules»
  // would send an operator to Config to write rules that are already there.
  const rulesConfigured = o?.profile_rules_configured === true;
  // The card modes and the avatar mode run different rules, and a project may
  // have either without the other.
  const cardRules = o?.profile_card_rules === true;
  const drawsAvatar = o?.profile_draws_avatar === true;
  // The overview can change under an open dialog, so the mode in effect is
  // always one of the cards actually drawn rather than whatever was picked
  // before they changed.
  const offered: ApplyProfileMode[] = [
    ...(cardRules ? (["fill", "reroll"] as const) : []),
    ...(drawsAvatar ? (["avatars"] as const) : []),
  ];
  const mode = offered.includes(picked) ? picked : (offered[0] ?? "fill");
  const nothingToFill = mode === "fill" && missingKeys === 0;

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["project", projectID, "bots"] });

  const toggle = useMutation({
    mutationFn: ({ bot, next }: { bot: PoolBot; next: boolean }) =>
      setBotEnabled(projectID, bot.player_id, next),
    onSuccess: () => void invalidate(),
    onError: (err) => push({ variant: "danger", message: botError(err) }),
  });

  const remove = useMutation({
    mutationFn: (bot: PoolBot) => removeBot(projectID, bot.player_id),
    onSuccess: (_data, bot) => {
      setPendingRemoval(null);
      push({ variant: "success", message: "Bot removed." });
      void invalidate();
      // Removal reaches past the bots screens: the player row is deleted, and
      // a bot in a guild is taken out of it first. Those screens are elsewhere
      // in the panel and none of them is mounted, so their caches are dropped
      // rather than flagged — an invalidation would refetch nothing, and the
      // next visit would open on the pre-removal answer.
      //
      // Bots are in the players list like anyone else — the store counts and
      // returns them without filtering on is_bot — and a row there opens a
      // card of its own, which would draw a full profile until the 404 lands.
      qc.removeQueries({ queryKey: playersListQueryKey(projectID) });
      qc.removeQueries({ queryKey: playerQueryKey(projectID, bot.player_id) });
      // The guild list goes whether or not THIS row says the bot had a guild.
      // The row is a snapshot of the last pool fetch, frozen again when the
      // dialog opened, while the server re-reads the membership as it removes
      // (bot.Service.RemoveBot → repo.GuildOf). The population worker joins
      // and leaves guilds on its own every few minutes, so "no guild" here can
      // be a guild there — and a guild list still counting a member who is
      // gone is the exact lie this drop exists for. The cost of being wrong
      // the other way is one refetch.
      qc.removeQueries({ queryKey: guildsListQueryKey(projectID) });
      // A card can only be dropped by id, and the only id here is that same
      // snapshot's: the DELETE answers 204 and names no guild. A bot that
      // changed guilds since the last pool fetch leaves the card of the one it
      // actually left behind — the narrower miss of the two, and the screen an
      // operator opens less often than the list.
      if (bot.guild_id) {
        qc.removeQueries({ queryKey: guildQueryKey(projectID, bot.guild_id) });
      }
    },
    onError: (err) => push({ variant: "danger", message: botError(err) }),
  });

  const apply = useMutation({
    mutationFn: (m: ApplyProfileMode) => applyBotProfile(projectID, m),
    // What actually happened, not what was asked for: `skipped` is the half of
    // the answer that says the run found less to do than the dialog offered,
    // and `partial` is the half that says it did not finish at all.
    onSuccess: (res, m) => {
      setApplyOpen(false);
      // A run that died half-way comes back 200 because the chunks before the
      // failure ARE committed. Reporting that as a success would leave an
      // operator believing a pool is done when a part of it still carries the
      // old card — and the fix is simply to run it again.
      push(
        res.partial
          ? {
              variant: "warn",
              title: "Profile applied in part",
              message: `${formatCount(res.applied)} of ${plural(
                res.planned ?? res.applied,
                "bot",
                "bots",
              )} updated — the run failed part-way, try again.`,
            }
          : {
              variant: "success",
              title: m === "avatars" ? "Avatars re-rolled" : "Profile applied",
              // …and what it deliberately did not do. The clause is left out
              // at zero: a project with no table would otherwise be told about
              // one on every run.
              message:
                (m === "fill"
                  ? `${plural(res.applied, "bot", "bots")} filled · ${formatCount(res.skipped)} already had every key`
                  : `${plural(res.applied, "bot", "bots")} re-rolled`) +
                (res.from_table > 0
                  ? ` · ${formatCount(res.from_table)} from the table left alone.`
                  : "."),
            },
      );
      void invalidate();
    },
    onError: (err) => push({ variant: "danger", message: botError(err) }),
  });

  function openApply() {
    setMode("fill");
    setApplyOpen(true);
  }

  // Six columns, so the pool fits the content width with no horizontal
  // scroll: every value that does not need a column of its own rides under
  // the one it belongs to. Mockup: bots-pool-fit.html.
  const columns: Column<PoolBot>[] = [
    {
      key: "bot",
      header: "Bot",
      width: "230px",
      cell: (b) => (
        <div className={styles.who}>
          <Avatar name={b.display_name} size={26} />
          <div className={styles.whoText}>
            <div className={styles.whoName}>{b.display_name}</div>
            {/* The avatar is the asset id the game resolves against its own
                catalogue, not a picture this panel has an address for. */}
            <div className={styles.whoSub}>
              {b.avatar ?? "— no avatar —"} · <CountryCell code={b.country} />
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "profile",
      header: "Profile",
      width: "128px",
      cell: (b) => (
        <div className={styles.badges}>
          <Badge variant={b.profile === "leader" ? "accent" : "neutral"}>
            {b.profile}
          </Badge>
          {/* Where this one came from — the first question asked of a bot
              whose card looks unexpected, which the player row cannot answer. */}
          {b.from_roster ? (
            <Badge variant="info">from table</Badge>
          ) : (
            <Badge variant="neutral">generated</Badge>
          )}
        </div>
      ),
    },
    {
      key: "guild",
      header: "Guild",
      width: "170px",
      cell: (b) =>
        b.guild_id ? (
          <Link
            className={styles.guildCell}
            to={`/projects/${projectID}/guilds/${b.guild_id}`}
            title={b.guild_name ?? undefined}
          >
            {b.guild_name}
          </Link>
        ) : (
          <span className={`${styles.free} ${styles.small}`}>— free —</span>
        ),
    },
    // The proof that the profile landed. Without it the only way to tell a
    // filled bot from an empty one is to open a player page per row.
    {
      key: "stats",
      header: "Stats",
      cell: (b) =>
        b.stats.length > 0 ? (
          <span className={styles.stats}>
            {b.stats.map((s, i) => (
              <Fragment key={s.key}>
                {i > 0 && " · "}
                <span className={styles.stat}>
                  <span className={styles.statKey}>{s.key}</span>{" "}
                  <span className={styles.statFigure}>
                    {formatCount(s.value)}
                  </span>
                </span>
              </Fragment>
            ))}
          </span>
        ) : (
          <span className={`${styles.free} ${styles.small}`}>— empty —</span>
        ),
    },
    {
      key: "state",
      header: "State",
      width: "170px",
      cell: (b) => {
        const st = botState(b);
        return (
          <div className={styles.stateCell}>
            <Badge variant={st.variant} dot>
              {st.label}
            </Badge>
            <span className={styles.lastAct}>
              {b.last_action_kind
                ? `${b.last_action_kind} · ${ago(b.last_action_at)}`
                : "—"}
            </span>
          </div>
        );
      },
    },
    {
      key: "actions",
      header: "",
      width: "60px",
      cell: (b) => (
        <div className={styles.rowActions}>
          <Menu
            aria-label={`Actions for ${b.display_name}`}
            items={[
              {
                label: b.enabled ? "Disable" : "Enable",
                icon: <Power size={14} />,
                onSelect: () => toggle.mutate({ bot: b, next: !b.enabled }),
              },
              {
                label: "Remove…",
                icon: <Trash2 size={14} />,
                variant: "danger",
                onSelect: () => setPendingRemoval(b),
              },
            ]}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      <AlertStack className={styles.alerts}>
        {withoutProfile > 0 && (
          <Alert
            variant="warn"
            title={`${formatCount(withoutProfile)} of ${formatCount(total)} bots ${
              withoutProfile === 1 ? "carries" : "carry"
            } no profile`}
          >
            They were created before <code>bots.profile</code> was configured,
            so their <code>meta</code> and <code>stats</code> are empty — in a
            roster they are the rows of zeros between real players.{" "}
            <button
              type="button"
              className={styles.alertLink}
              onClick={openApply}
            >
              Apply the profile →
            </button>
          </Alert>
        )}
      </AlertStack>

      <div className={styles.toolbar}>
        <div className={styles.search}>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name…"
            aria-label="Search bots by name"
          />
        </div>
        <Select
          value={profile}
          onChange={setProfile}
          options={[
            { value: "", label: "Profile: all" },
            { value: "casual", label: "casual", mono: true },
            { value: "active", label: "active", mono: true },
            { value: "social", label: "social", mono: true },
            { value: "leader", label: "leader", mono: true },
          ]}
        />
        <Select
          value={state}
          onChange={setState}
          options={[
            { value: "", label: "State: all" },
            { value: "active", label: "active" },
            { value: "cooldown", label: "on cooldown" },
            { value: "free", label: "free" },
            { value: "disabled", label: "disabled" },
          ]}
        />
        <span className={styles.spacer} />
        {/* Offered whether or not the banner is — a re-roll after changing
            the ranges is wanted on a pool with no gaps at all. It waits for
            the overview, though: every sentence in the dialog is one of those
            numbers, and a dialog opened without them can only guess. */}
        <Button
          variant="secondary"
          icon={<RefreshCw size={14} />}
          disabled={overviewLoading}
          onClick={openApply}
        >
          Apply profile…
        </Button>
      </div>

      {o && <SourceStrip projectID={projectID} o={o} />}

      <div className={styles.card}>
        <Table
          columns={columns}
          rows={data?.bots ?? []}
          rowClassName={(b) => (b.enabled ? undefined : styles.bad)}
          empty={
            isLoading
              ? "Loading…"
              : "No bots match. The population worker creates them as guilds fall below the fill threshold."
          }
        />
        <div className={styles.cardFoot}>
          <span>
            {data ? `${data.total} bots · showing ${data.bots.length}` : "—"}
          </span>
          <span className={styles.mono}>
            players.is_bot = true · never exposed by the game API
          </span>
        </div>
      </div>

      {pendingRemoval && (
        <Modal
          open
          title={`Remove ${pendingRemoval.display_name}?`}
          onClose={() => setPendingRemoval(null)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setPendingRemoval(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                loading={remove.isPending}
                onClick={() => remove.mutate(pendingRemoval)}
              >
                Remove bot
              </Button>
            </>
          }
        >
          <p>
            The bot leaves its guild and its player row is deleted, along with
            every request and donation it had queued.
          </p>
          <p>
            Its chat messages stay. Deleting them would tear holes in a history
            real players have read and replied to — which is a worse outcome
            than a few lines signed by a name that no longer resolves.
          </p>
        </Modal>
      )}

      {applyOpen && (
        <Modal
          open
          width="sm"
          title="Apply profile to the pool"
          icon={<RefreshCw size={17} />}
          onClose={() => setApplyOpen(false)}
          closeDisabled={apply.isPending}
          footer={
            <>
              {rulesConfigured && (
                <span className={styles.footNote}>
                  {mode === "fill"
                    ? `bots missing a key: ${formatCount(missingKeys)}`
                    : `${plural(total, "bot", "bots")} · ${formatCount(inPopulated)} in populated guilds`}
                </span>
              )}
              <Button
                variant="secondary"
                onClick={() => setApplyOpen(false)}
                disabled={apply.isPending}
              >
                Cancel
              </Button>
              {/* Nothing to fill is not a run worth making: it would report
                  «0 bots filled» over a pool it never touched. */}
              <Button
                loading={apply.isPending}
                disabled={!rulesConfigured || nothingToFill}
                onClick={() => apply.mutate(mode)}
              >
                {mode === "fill"
                  ? `Apply to ${plural(missingKeys, "bot", "bots")}`
                  : mode === "avatars"
                    ? `Re-roll ${plural(total, "avatar", "avatars")}`
                    : `Re-roll ${plural(total, "bot", "bots")}`}
              </Button>
            </>
          }
        >
          <div className={styles.applyBody}>
            {!o ? (
              /* We do not know yet, or we asked and were not told. Neither is
                 «this project has no rules», and saying so would send an
                 operator off to write rules that may already be there. */
              overviewFailed ? (
                <Alert
                  variant="danger"
                  title="This project's configuration could not be read"
                >
                  Every sentence in this dialog is a number from the bots
                  overview, and that request failed — so there is nothing
                  honest to offer and nothing has been run. Reload the page and
                  try again.
                </Alert>
              ) : (
                <p className={styles.applyLead}>
                  Reading this project's configuration…
                </p>
              )
            ) : rulesConfigured ? (
              <>
                <p className={styles.applyLead}>
                  The rules in{" "}
                  <Link to={sourceLink(projectID)}>Source → Profile</Link>{" "}
                  apply to every bot the worker creates from now on. This runs
                  them over the {plural(total, "bot", "bots")} you already have.
                </p>
                <div className={styles.radioCards} role="radiogroup">
                  {cardRules && (
                    <>
                      <ModeCard
                        value="fill"
                        current={mode}
                        onSelect={setMode}
                        glyph={<Plus size={15} />}
                        title="Fill missing keys only"
                        count={plural(missingKeys, "bot", "bots")}
                        desc="A key a bot already carries is left exactly as it is — including whatever it has grown to and whatever you edited by hand. Nobody in a guild changes appearance."
                      />
                      <ModeCard
                        value="reroll"
                        current={mode}
                        onSelect={setMode}
                        glyph={<RefreshCw size={15} />}
                        title="Re-roll everything"
                        count={plural(total, "bot", "bots")}
                        desc={
                          drawsAvatar
                            ? "Every configured key and the avatar are drawn again from the current rules. This is the one to use after changing the ranges."
                            : "Every configured key is drawn again from the current rules. This is the one to use after changing the ranges."
                        }
                      />
                    </>
                  )}
                  {drawsAvatar && (
                    <ModeCard
                      value="avatars"
                      current={mode}
                      onSelect={setMode}
                      glyph={<UserRound size={15} />}
                      title="Re-roll avatars only"
                      count={plural(total, "bot", "bots")}
                      desc="Every bot draws its avatar again from the Avatar rule. Stats and meta stay exactly as they are, and so does the growth clock."
                    />
                  )}
                </div>
                {/* Not one of the three below, and not exclusive with any of
                    them: it is about what the run will NOT do, in any mode,
                    and it has to be said before the button rather than
                    reported by the toast afterwards.
                    (t-103/bots-source.html §5.) */}
                {fromTable > 0 && (
                  <Alert
                    variant="warn"
                    title={`${plural(fromTable, "bot", "bots")} came from the table and will be left alone`}
                  >
                    A bot whose card came from the source table was never
                    rolled — an operator typed it — so re-rolling it would
                    erase what they wrote, and filling it would write over keys
                    somebody chose. No mode touches them, so the run
                    changes fewer bots than the count on the button.{" "}
                    <Link to={sourceLink(projectID)}>Open Source →</Link>
                  </Alert>
                )}
                {/* The warning is what re-rolling costs, so it is only earned
                    while there is somebody to pay it. On a project still in
                    its cold start nobody has seen these bots, and an alarm
                    over zero players would teach an operator to ignore it. */}
                {nothingToFill ? (
                  <Alert variant="info" title="There is nothing to fill">
                    Every bot already carries every key the rules cover, so
                    this mode would look at the pool and write nothing. A
                    re-roll is the one that changes cards that are already
                    there.
                  </Alert>
                ) : mode !== "fill" && inPopulated > 0 ? (
                  <Alert
                    variant="warn"
                    title={`${plural(inPopulated, "bot is", "bots are")} sitting in guilds with living players`}
                  >
                    {mode === "avatars" ? (
                      <>
                        Their picture changes under a player who may have seen
                        it yesterday. Their numbers do not.
                      </>
                    ) : (
                      <>
                        Their cosmetics and numbers change under a player who
                        may have seen them yesterday. On a project that is past
                        its cold start, prefer filling the gaps.
                      </>
                    )}
                  </Alert>
                ) : (
                  cardRules && (
                    <Alert variant="info">
                      Keys with no rule are never touched, in any mode — the
                      platform does not delete what it did not write.
                    </Alert>
                  )
                )}
                {/* The card modes are not offered at all here, so the dialog
                    says why rather than leaving them to be missed
                    (bots-pool-reroll-avatars.html §3). */}
                {!cardRules && (
                  <Alert variant="info">
                    <code>bots.profile</code> has no stats or meta rule, so the
                    avatar is the only thing there is to apply.{" "}
                    <Link to={sourceLink(projectID)}>Open Source → Profile</Link>
                  </Alert>
                )}
              </>
            ) : (
              /* Not in the mockup, because the mockup draws the pool this
                 feature exists for. The button is offered from every state, so
                 the one state with nothing to run has to say so rather than
                 fail on the server. */
              <Alert variant="warn" title="No profile rules are configured">
                <code>bots.profile</code> holds no rule, so there is nothing to
                run over the pool. Say which keys a bot should carry first.{" "}
                <Link to={sourceLink(projectID)}>Open Source → Profile</Link>
              </Alert>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}

/**
 * Where these bots came from, and how many more there can be — one line above
 * the table (t-103/bots-source.html §6).
 *
 * The numbers come from the overview this tab already loads, so the strip costs
 * no request of its own; that is also why the pool listing carries no summary.
 * In `generated` mode it says only that, and prints no counts at all: the five
 * source numbers are zero there, and «0 rows left» about a project with no
 * table is an alarm about nothing.
 */
function SourceStrip({
  projectID,
  o,
}: {
  projectID: string;
  o: { source: BotSource; source_left: number; source_blocked: number };
}) {
  const table = o.source === "table";
  return (
    <div className={styles.sourceStrip}>
      <span>
        <b>Source:</b> {SOURCE_LABEL[o.source]}
      </span>
      {table && (
        <>
          <span className={styles.dotSep}>·</span>
          <span>{plural(o.source_left, "row", "rows")} left</span>
          {/* Nothing blocked is nothing to say. An amber «0 blocked» on every
              healthy project is how an operator learns to stop reading it. */}
          {o.source_blocked > 0 && (
            <>
              <span className={styles.dotSep}>·</span>
              <span className={styles.warnText}>
                {formatCount(o.source_blocked)} blocked
              </span>
            </>
          )}
        </>
      )}
      <span className={styles.spacer} />
      <Link className={styles.layerLink} to={sourceLink(projectID)}>
        Open Source →
      </Link>
    </div>
  );
}

/** One mode, as a radio card (bots-pool-apply-profile.html §2). */
function ModeCard({
  value,
  current,
  glyph,
  title,
  count,
  desc,
  onSelect,
}: {
  value: ApplyProfileMode;
  current: ApplyProfileMode;
  glyph: ReactNode;
  title: string;
  count: string;
  desc: string;
  onSelect: (v: ApplyProfileMode) => void;
}) {
  const selected = value === current;
  return (
    <label className={`${styles.option} ${selected ? styles.selected : ""}`}>
      <input
        type="radio"
        name="apply-profile-mode"
        value={value}
        checked={selected}
        onChange={() => onSelect(value)}
      />
      <span className={styles.glyphBox} aria-hidden="true">
        {glyph}
      </span>
      <span className={styles.optBody}>
        <span className={styles.optTitle}>
          {title} <span className={styles.optCount}>{count}</span>
        </span>
        <span className={styles.optDesc}>{desc}</span>
      </span>
    </label>
  );
}

function botState(b: PoolBot): {
  label: string;
  variant: "success" | "warn" | "neutral" | "danger";
} {
  if (!b.enabled) return { label: "disabled", variant: "danger" };
  if (!b.guild_id) {
    return { label: `waiting ${ago(b.left_at)}`, variant: "neutral" };
  }
  if (b.cooldown_until && Date.parse(b.cooldown_until) > Date.now()) {
    const mins = Math.max(
      1,
      Math.round((Date.parse(b.cooldown_until) - Date.now()) / 60_000),
    );
    return { label: `cooldown ${mins}m`, variant: "warn" };
  }
  return { label: "active", variant: "success" };
}
